import React, { useEffect, useState, useCallback, useRef } from "react";
import routeGames from "./routes";
import { useSelector } from "react-redux";
import { routeAuth } from "../AuthPage";
import { Redirect } from "react-router-dom";
import { getRequest, postRequest, putRequest } from "../../services/requestController";

import "./styles.scss";

import {
    Badge,
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    Form,
    InputNumber,
    Modal,
    Popconfirm,
    Progress,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Switch,
    Tabs,
    Tag,
    Typography,
    notification,
} from "antd";
import { UserHeader } from "../../components/UserHeader/UserHeader";

import {
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
    SettingOutlined,
    ThunderboltOutlined,
    TrophyOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

const CATEGORY_NAMES = {
    MASS: 'Массовка',
    LUCKY: 'Удачливые',
    LEGENDS: 'Легенды',
};

const CATEGORY_COLORS = {
    MASS: '#1677ff',
    LUCKY: '#52c41a',
    LEGENDS: '#faad14',
};

const TEMPERATURE_NAMES = {
    hot: '🔥 Горячий',
    cold: '❄️ Холодный',
    neutral: '🌡️ Нейтральный',
};

const PiggyTapDemo = ({ open, onClose, token }) => {
    const [loading, setLoading] = useState(false);
    const [gameData, setGameData] = useState(null);
    const [currentTap, setCurrentTap] = useState(0);
    const [earnedPoints, setEarnedPoints] = useState(0);
    const [lastTapValue, setLastTapValue] = useState(null);
    const [jackpotWon, setJackpotWon] = useState(null);
    const [isComplete, setIsComplete] = useState(false);
    const [tapHistory, setTapHistory] = useState([]);
    const gameDataRef = useRef(null);

    const resetState = () => {
        setCurrentTap(0);
        setEarnedPoints(0);
        setLastTapValue(null);
        setJackpotWon(null);
        setIsComplete(false);
        setTapHistory([]);
    };

    const startDemo = useCallback(async () => {
        setLoading(true);
        resetState();
        try {
            const res = await postRequest("/api/admin/tap/demo", {}, token);
            if (res?.success && res.data?.success && res.data?.data) {
                setGameData(res.data.data);
                gameDataRef.current = res.data.data;
            } else {
                notification.error({
                    message: "Ошибка",
                    description: res?.data?.message || res?.error?.message || "Не удалось загрузить демо",
                });
            }
        } catch {
            notification.error({ message: "Ошибка", description: "Не удалось загрузить демо" });
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (open) startDemo();
        return () => {
            setGameData(null);
            gameDataRef.current = null;
            resetState();
        };
    }, [open, startDemo]);

    const handleTap = () => {
        const data = gameDataRef.current;
        if (!data || currentTap >= data.totalTaps || isComplete) return;

        const tapValue = data.taps[currentTap];
        const newTap = currentTap + 1;
        const newPoints = earnedPoints + tapValue;

        setCurrentTap(newTap);
        setEarnedPoints(newPoints);
        setLastTapValue(tapValue);
        setTapHistory((prev) => [...prev, tapValue]);

        if (data.jackpot && data.jackpot.position === currentTap) {
            setJackpotWon(data.jackpot);
        }

        if (newTap >= data.totalTaps) setIsComplete(true);
    };

    const handleClose = () => {
        setGameData(null);
        gameDataRef.current = null;
        resetState();
        onClose();
    };

    const progressPercent = gameData ? Math.round((currentTap / gameData.totalTaps) * 100) : 0;

    return (
        <Modal
            open={open}
            title={
                <Space>
                    <span role="img" aria-label="pig">🐷</span>
                    Piggy Tap — Демо
                </Space>
            }
            onCancel={handleClose}
            footer={null}
            width={600}
            destroyOnClose
        >
            {loading ? (
                <div style={{ textAlign: "center", padding: 60 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}><Text>Загрузка игры...</Text></div>
                </div>
            ) : !gameData ? (
                <div style={{ textAlign: "center", padding: 40 }}>
                    <Button type="primary" onClick={startDemo} icon={<PlayCircleOutlined />}>
                        Начать демо
                    </Button>
                </div>
            ) : (
                <Space direction="vertical" size={20} style={{ width: "100%" }}>
                    {gameData.category && (
                        <Card bordered={false} style={{ background: "#f0f5ff" }}>
                            <Row gutter={16} align="middle">
                                <Col span={8}>
                                    <Space direction="vertical" size={0}>
                                        <Text type="secondary">Категория</Text>
                                        <Tag color={CATEGORY_COLORS[gameData.category]} style={{ fontSize: 14 }}>
                                            {CATEGORY_NAMES[gameData.category]}
                                            {gameData.group && ` (Группа ${gameData.group})`}
                                        </Tag>
                                    </Space>
                                </Col>
                                <Col span={8}>
                                    <Space direction="vertical" size={0}>
                                        <Text type="secondary">Целевой приз</Text>
                                        <Text strong style={{ fontSize: 16, color: CATEGORY_COLORS[gameData.category] }}>
                                            {gameData.targetPoints?.toLocaleString("ru-RU")} баллов
                                        </Text>
                                    </Space>
                                </Col>
                                <Col span={8}>
                                    <Space direction="vertical" size={0}>
                                        <Text type="secondary">Сегмент</Text>
                                        <Text style={{ fontSize: 14 }}>
                                            {TEMPERATURE_NAMES[gameData.segmentTemperature] || gameData.segmentTemperature}
                                        </Text>
                                    </Space>
                                </Col>
                            </Row>
                        </Card>
                    )}

                    <Card bordered={false} style={{ background: "#fef6e4" }}>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Statistic title="Тапов" value={currentTap} suffix={`/ ${gameData.totalTaps}`} />
                            </Col>
                            <Col span={8}>
                                <Statistic
                                    title="Промобаллы"
                                    value={earnedPoints}
                                    valueStyle={{ color: "#52c41a" }}
                                />
                            </Col>
                            <Col span={8}>
                                <Statistic
                                    title="Последний тап"
                                    value={lastTapValue !== null ? `+${lastTapValue}` : "—"}
                                    valueStyle={{ color: lastTapValue > 0 ? "#1677ff" : "#999" }}
                                />
                            </Col>
                        </Row>
                    </Card>

                    <div style={{ textAlign: "center" }}>
                        <Progress
                            percent={progressPercent}
                            status={isComplete ? "success" : "active"}
                            strokeColor={{ "0%": "#ff85c0", "100%": "#ff4d94" }}
                        />
                    </div>

                    {jackpotWon && (
                        <Card bordered={false} style={{ background: "#fff7e6", textAlign: "center" }}>
                            <Space direction="vertical">
                                <TrophyOutlined style={{ fontSize: 48, color: "#faad14" }} />
                                <Title level={4} style={{ margin: 0, color: "#d48806" }}>ДЖЕКПОТ!</Title>
                                <Text strong style={{ fontSize: 24 }}>
                                    {jackpotWon.amount.toLocaleString("ru-RU")} ₽
                                </Text>
                                <Text type="secondary">Вы претендуете на участие в розыгрыше!</Text>
                            </Space>
                        </Card>
                    )}

                    {isComplete ? (
                        <Card bordered={false} style={{ background: "#f6ffed", textAlign: "center" }}>
                            <Space direction="vertical" size={12}>
                                <CheckCircleOutlined style={{ fontSize: 48, color: "#52c41a" }} />
                                <Title level={4} style={{ margin: 0 }}>Игра завершена!</Title>
                                <Descriptions column={1} bordered size="small">
                                    {gameData.category && (
                                        <Descriptions.Item label="Категория">
                                            <Tag color={CATEGORY_COLORS[gameData.category]}>
                                                {CATEGORY_NAMES[gameData.category]}
                                                {gameData.group && ` (${gameData.group})`}
                                            </Tag>
                                        </Descriptions.Item>
                                    )}
                                    <Descriptions.Item label="Целевой приз">
                                        <Text style={{ color: "#666" }}>
                                            {gameData.targetPoints?.toLocaleString("ru-RU")} баллов
                                        </Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Промобаллы">
                                        <Text strong style={{ color: "#52c41a", fontSize: 18 }}>
                                            {earnedPoints.toLocaleString("ru-RU")} баллов
                                        </Text>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Эквивалент">
                                        {(earnedPoints * 0.05).toLocaleString("ru-RU")} BRU
                                    </Descriptions.Item>
                                    {jackpotWon && (
                                        <Descriptions.Item label="Джекпот">
                                            <Tag color="gold">{jackpotWon.amount.toLocaleString("ru-RU")} ₽</Tag>
                                        </Descriptions.Item>
                                    )}
                                </Descriptions>
                                <Button type="primary" onClick={startDemo} icon={<ReloadOutlined />}>
                                    Играть снова
                                </Button>
                            </Space>
                        </Card>
                    ) : (
                        <div style={{ textAlign: "center" }}>
                            <Button
                                type="primary"
                                size="large"
                                onClick={handleTap}
                                style={{
                                    width: 200,
                                    height: 200,
                                    borderRadius: "50%",
                                    fontSize: 64,
                                    background: "linear-gradient(135deg, #ff85c0 0%, #ff4d94 100%)",
                                    border: "none",
                                    boxShadow: "0 8px 24px rgba(255, 77, 148, 0.4)",
                                }}
                            >
                                🐷
                            </Button>
                            <div style={{ marginTop: 16 }}>
                                <Text type="secondary">Тапните по свинке!</Text>
                            </div>
                        </div>
                    )}

                    {tapHistory.length > 0 && (
                        <Card bordered={false} title="Последние тапы" size="small">
                            <Space wrap>
                                {tapHistory.slice(-20).map((v, i) => (
                                    <Tag key={i} color={v > 0 ? "blue" : "default"}>
                                        {v > 0 ? `+${v}` : "0"}
                                    </Tag>
                                ))}
                            </Space>
                        </Card>
                    )}
                </Space>
            )}
        </Modal>
    );
};

const GamesPage = () => {
    const token = useSelector((state) => state.token);

    const [settings, setSettings] = useState(null);
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [demoOpen, setDemoOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [settingsSubmitting, setSettingsSubmitting] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [activeTab, setActiveTab] = useState("piggy-tap");

    const [form] = Form.useForm();

    const loadSettings = useCallback(async () => {
        setSettingsLoading(true);
        try {
            const res = await getRequest("/api/admin/tap/settings", token);
            if (res?.success && res.data?.success && res.data?.data) {
                setSettings(res.data.data);
            } else {
                notification.error({ message: "Ошибка", description: res?.data?.message || "Не удалось загрузить настройки" });
            }
        } catch {
            notification.error({ message: "Ошибка", description: "Не удалось загрузить настройки" });
        } finally {
            setSettingsLoading(false);
        }
    }, [token]);

    useEffect(() => { if (token) loadSettings(); }, [token, loadSettings]);

    const handleToggle = async (enabled) => {
        try {
            const res = await putRequest("/api/admin/tap/settings", { enabled }, token);
            if (res?.success && res.data?.success && res.data?.data) {
                setSettings(res.data.data);
                notification.success({ message: "Готово", description: enabled ? "Игра включена" : "Игра выключена" });
            } else {
                notification.error({ message: "Ошибка", description: res?.data?.message || "Не удалось обновить" });
            }
        } catch {
            notification.error({ message: "Ошибка", description: "Не удалось обновить статус" });
        }
    };

    const openSettingsModal = () => {
        if (settings) {
            form.setFieldsValue({
                tapsPerPlayer: settings.tapsPerPlayer,
                zeroPercent: settings.zeroPercent,
                algorithm: settings.algorithm || 'STANDARD',
                aggressiveCycleGames: settings.aggressiveCycleGames ?? 100,
                aggressiveCyclePrizePool: settings.aggressiveCyclePrizePool ?? 6945,
            });
        }
        setSettingsOpen(true);
    };

    const handleSettingsSubmit = async (values) => {
        setSettingsSubmitting(true);
        try {
            const res = await putRequest("/api/admin/tap/settings", values, token);
            if (res?.success && res.data?.success && res.data?.data) {
                setSettings(res.data.data);
                setSettingsOpen(false);
                notification.success({ message: "Успешно", description: res.data.message || "Настройки сохранены" });
            } else {
                notification.error({
                    message: "Ошибка",
                    description: res?.data?.message || res?.error?.message || "Не удалось сохранить",
                });
            }
        } catch {
            notification.error({ message: "Ошибка", description: "Не удалось сохранить настройки" });
        } finally {
            setSettingsSubmitting(false);
        }
    };

    const handleResetPool = async () => {
        setResetting(true);
        try {
            const res = await postRequest("/api/admin/tap/reset-pool", {}, token);
            if (res?.success && res.data?.success) {
                notification.success({ message: "Готово", description: res.data.message || "Пул тапов сброшен" });
                loadSettings();
            } else {
                notification.error({ message: "Ошибка", description: res?.data?.message || "Не удалось сбросить" });
            }
        } catch {
            notification.error({ message: "Ошибка", description: "Не удалось сбросить пул" });
        } finally {
            setResetting(false);
        }
    };

    if (!token) return <Redirect to={routeAuth()} />;

    const cycleGamesTotal = settings?.activeCycleGamesTotal ?? settings?.aggressiveCycleGames ?? 100;
    const cycleGamesPlayed = settings?.activeCycleGamesPlayed ?? 0;
    const usedPercent = settings && cycleGamesTotal > 0
        ? Math.min(100, Math.round((cycleGamesPlayed / cycleGamesTotal) * 100))
        : 0;

    return (
        <>
            <div className="container games-page">
                <UserHeader />

                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} md={12}>
                        <Typography>
                            <Title level={3} style={{ marginBottom: 0 }}>Игры</Title>
                            <Text type="secondary">Управление играми</Text>
                        </Typography>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: "right" }}>
                        <Button onClick={loadSettings} icon={<ReloadOutlined />} loading={settingsLoading}>
                            Обновить
                        </Button>
                    </Col>
                </Row>

                <Divider />

                <Tabs defaultActiveKey="piggy-tap" items={[
                    {
                        key: "piggy-tap",
                        label: <span>🐷 Piggy Tap</span>,
                        children: null,
                    },
                    {
                        key: "piggy-mine",
                        label: <span>⛏ Piggy Mine</span>,
                        children: null,
                    },
                ]} onChange={(key) => setActiveTab(key)} />

                {activeTab === "piggy-tap" && (settingsLoading ? (
                    <div style={{ textAlign: "center", padding: 60 }}>
                        <Spin size="large" />
                    </div>
                ) : settings ? (
                    <Row gutter={[16, 16]}>
                        <Col xs={24}>
                            <Card
                                title={
                                    <Space align="center" size={12}>
                                        <Badge status={settings.enabled ? "success" : "default"} />
                                        <span role="img" aria-label="pig" style={{ fontSize: 24 }}>🐷</span>
                                        <span>Piggy Tap (Свинка-тапалка)</span>
                                        <Tag color={settings.enabled ? "green" : "volcano"}>
                                            {settings.enabled ? "ВКЛЮЧЕНА" : "ВЫКЛЮЧЕНА"}
                                        </Tag>
                                        {settings.algorithm && (
                                            <Tag color={settings.algorithm === "AGGRESSIVE" ? "purple" : "blue"}>
                                                {settings.algorithm}
                                            </Tag>
                                        )}
                                    </Space>
                                }
                                extra={
                                    <Space>
                                        <Switch
                                            checked={settings.enabled}
                                            onChange={handleToggle}
                                            checkedChildren="ON"
                                            unCheckedChildren="OFF"
                                        />
                                        <Button icon={<SettingOutlined />} onClick={openSettingsModal}>
                                            Настройки
                                        </Button>
                                        <Button type="primary" icon={<PlayCircleOutlined />} onClick={() => setDemoOpen(true)}>
                                            Демо
                                        </Button>
                                    </Space>
                                }
                            >
                                <Text type="secondary">
                                    Мини-игра для новых пользователей. Игрок получает определённое количество тапов
                                    и натапывает промобаллы, которые сможет потом поставить на игру.
                                </Text>

                                <Divider />

                                <Row gutter={[16, 16]}>
                                    <Col xs={24} md={6}>
                                        <Card bordered={false}>
                                            <Statistic title="Тапов на игрока" value={settings.tapsPerPlayer} />
                                        </Card>
                                    </Col>
                                    <Col xs={24} md={6}>
                                        <Card bordered={false}>
                                            <Statistic
                                                title="Игр в цикле"
                                                value={settings.aggressiveCycleGames ?? 100}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} md={6}>
                                        <Card bordered={false}>
                                            <Statistic
                                                title="Пул за цикл"
                                                value={(settings.aggressiveCyclePrizePool ?? 6945).toLocaleString("ru-RU")}
                                                suffix="BRU"
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} md={6}>
                                        <Card bordered={false}>
                                            <Statistic title="Нулевых тапов" value={`${settings.zeroPercent}%`} />
                                        </Card>
                                    </Col>
                                </Row>

                                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                                    <Col xs={24}>
                                        <Card bordered={false} title="Заполнение цикла">
                                            <Row gutter={16} align="middle">
                                                <Col span={16}>
                                                    <Progress
                                                        percent={usedPercent}
                                                        status={usedPercent >= 90 ? "exception" : "active"}
                                                        strokeColor={usedPercent >= 90 ? "#ff4d4f" : "#1677ff"}
                                                    />
                                                </Col>
                                                <Col span={8}>
                                                    <Space>
                                                        <Text>
                                                            {cycleGamesPlayed} / {cycleGamesTotal} игр
                                                        </Text>
                                                        <Popconfirm
                                                            title="Сбросить цикл?"
                                                            description="Все активные сессии будут завершены, начнётся новый цикл"
                                                            onConfirm={handleResetPool}
                                                            okText="Сбросить"
                                                            cancelText="Отмена"
                                                            icon={<ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />}
                                                            okButtonProps={{ danger: true }}
                                                        >
                                                            <Button danger loading={resetting}>
                                                                Сбросить цикл
                                                            </Button>
                                                        </Popconfirm>
                                                    </Space>
                                                </Col>
                                            </Row>
                                        </Card>
                                    </Col>
                                </Row>

                                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                                    <Col xs={24}>
                                        <Card
                                            bordered={false}
                                            title={
                                                <Space>
                                                    <TrophyOutlined style={{ color: "#faad14" }} />
                                                    <span>Общий пул выигрыша</span>
                                                    {settings.activeCycleNumber != null ? (
                                                        <Tag color="purple">
                                                            Цикл #{settings.activeCycleNumber}
                                                            {settings.activeCycleGamesPlayed != null
                                                                ? ` · ${settings.activeCycleGamesPlayed}/${settings.activeCycleGamesTotal ?? settings.aggressiveCycleGames ?? 100} игр`
                                                                : ""}
                                                        </Tag>
                                                    ) : (
                                                        <Tag color="default">Нет активного цикла</Tag>
                                                    )}
                                                </Space>
                                            }
                                        >
                                            {(() => {
                                                const total = settings.aggressivePrizePool ?? 6945;
                                                const hasActive = settings.activeCycleNumber != null;
                                                const won = hasActive ? (settings.aggressiveWonBRU ?? 0) : 0;
                                                const remaining = hasActive
                                                    ? (settings.aggressiveRemainingBRU ?? Math.max(0, total - won))
                                                    : total;
                                                const percent = total > 0 ? Math.min(100, Math.round((won / total) * 100)) : 0;
                                                return (
                                                    <>
                                                        <Row gutter={[16, 16]}>
                                                            <Col xs={24} md={8}>
                                                                <Statistic
                                                                    title="Общий пул (за цикл)"
                                                                    value={total}
                                                                    suffix="BRU"
                                                                    valueStyle={{ color: "#1677ff" }}
                                                                />
                                                            </Col>
                                                            <Col xs={24} md={8}>
                                                                <Statistic
                                                                    title="Выиграно в активном цикле"
                                                                    value={won}
                                                                    suffix="BRU"
                                                                    valueStyle={{ color: "#52c41a" }}
                                                                />
                                                            </Col>
                                                            <Col xs={24} md={8}>
                                                                <Statistic
                                                                    title="Осталось в цикле"
                                                                    value={remaining}
                                                                    suffix="BRU"
                                                                    valueStyle={{ color: "#faad14" }}
                                                                />
                                                            </Col>
                                                        </Row>
                                                        <div style={{ marginTop: 16 }}>
                                                            <Progress
                                                                percent={percent}
                                                                strokeColor={{ "0%": "#52c41a", "100%": "#faad14" }}
                                                                format={(p) => `${p}%`}
                                                            />
                                                        </div>
                                                        {!hasActive && (
                                                            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
                                                                Нет активного цикла — статистика обнулена. Новый цикл начнётся с первой AGGRESSIVE-игры.
                                                            </Text>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </Card>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                    </Row>
                ) : (
                    <Card>
                        <Text type="secondary">Не удалось загрузить настройки</Text>
                    </Card>
                ))}

                {activeTab === "piggy-mine" && <PiggyMineTab token={token} />}
            </div>

            <PiggyTapDemo open={demoOpen} onClose={() => setDemoOpen(false)} token={token} />

            <Modal
                open={settingsOpen}
                title="Настройки Piggy Tap"
                onCancel={() => setSettingsOpen(false)}
                onOk={() => form.submit()}
                okText="Сохранить"
                cancelText="Отмена"
                confirmLoading={settingsSubmitting}
                width={600}
            >
                <Form form={form} layout="vertical" onFinish={handleSettingsSubmit}>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="tapsPerPlayer"
                                label="Тапов на игрока"
                                rules={[{ required: true, message: "Обязательное поле" }]}
                                tooltip="Рекомендуется 200 тапов для корректной работы алгоритма. Общий пул тапов рассчитывается автоматически как «Количество игр в цикле × тапов на игрока»."
                            >
                                <InputNumber min={50} max={500} style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        name="zeroPercent"
                        label="Базовый процент нулевых тапов"
                        rules={[{ required: true, message: "Обязательное поле" }]}
                        tooltip="30% — рекомендуемое значение"
                    >
                        <InputNumber min={0} max={50} style={{ width: "100%" }} addonAfter="%" />
                    </Form.Item>
                    <Form.Item
                        name="algorithm"
                        label="Алгоритм призового фонда"
                        tooltip="STANDARD: 500 игр (MASS/LUCKY/LEGENDS) | AGGRESSIVE: настраиваемый цикл (L1/L2/L3) с джекпотами через admin-розыгрыш"
                    >
                        <Select
                            options={[
                                { value: 'STANDARD', label: 'STANDARD — 500 игр (классический)' },
                                { value: 'AGGRESSIVE', label: 'AGGRESSIVE — настраиваемый цикл, L1/L2/L3 + джекпоты' },
                            ]}
                        />
                    </Form.Item>

                    <Divider orientation="left" plain>AGGRESSIVE · параметры цикла</Divider>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="aggressiveCycleGames"
                                label="Количество игр в цикле"
                                tooltip="Длина одного цикла. Распределение по уровням сохраняется (L1: 35%, L2: 45%, L3: 20%) независимо от количества игр."
                            >
                                <InputNumber min={10} max={1000} step={10} style={{ width: "100%" }} addonAfter="игр" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="aggressiveCyclePrizePool"
                                label="Общий пул выигрыша (за цикл)"
                                tooltip="Суммарный пул игровых выплат за весь цикл в BRU. Джекпоты разыгрываются отдельно и в этот пул не входят. Фактическая сумма может слегка отклоняться от заданной (погрешность небольшая)."
                            >
                                <InputNumber min={0} max={1000000} step={100} style={{ width: "100%" }} addonAfter="BRU" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
                        Диапазоны выигрышей по группам (фиксированы):
                        L1 — 10–50 BRU (малые), L2 — 50–100 BRU (средние), L3 — 100–200 BRU (крупные).
                        Распределение игр: 35% / 45% / 20%.
                    </Text>
                </Form>
            </Modal>
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Piggy Mine Tab Component
// ═══════════════════════════════════════════════════════════════════════════════

const PiggyMineTab = ({ token }) => {
    const [settings, setSettings] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);
    const [demoModalOpen, setDemoModalOpen] = useState(false);
    const [form] = Form.useForm();

    const fetchSettings = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const res = await getRequest("/api/admin/piggy-mine/settings", token);
            if (res?.success && res?.data?.data) {
                setSettings(res.data.data);
            }
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    }, [token]);

    const fetchStats = useCallback(async () => {
        if (!token) return;
        try {
            const res = await getRequest("/api/admin/piggy-mine/stats", token);
            if (res?.success && res?.data?.data) {
                setStats(res.data.data);
            }
        } catch (err) {
            console.error(err);
        }
    }, [token]);

    useEffect(() => {
        fetchSettings();
        fetchStats();
    }, [fetchSettings, fetchStats]);

    const toggleEnabled = async (enabled) => {
        const res = await putRequest("/api/admin/piggy-mine/settings", { enabled }, token);
        if (res?.success) {
            setSettings((prev) => ({ ...prev, enabled }));
            notification.success({ message: enabled ? "Игра включена" : "Игра выключена" });
        }
    };

    const openSettingsModal = () => {
        if (settings) {
            form.setFieldsValue({
                entryFee: settings.entryFee,
                coinPrizeMin: settings.coinPrizeMin,
                coinPrizeMax: settings.coinPrizeMax,
                dollarCoinPrize: settings.dollarCoinPrize,
                dollarCoinChance: settings.dollarCoinChance,
                dynamiteChance: settings.dynamiteChance,
                scatterChance: settings.scatterChance,
                blockageMinDistance: settings.blockageMinDistance,
                firstBlockageMinSegment: settings.firstBlockageMinSegment,
                bonusLampDurationMin: settings.bonusLampDurationMin,
                bonusLampDurationMax: settings.bonusLampDurationMax,
                bonusTapRewardMin: settings.bonusTapRewardMin,
                bonusTapRewardMax: settings.bonusTapRewardMax,
                maxSegments: settings.maxSegments,
                tunnelChance: settings.tunnelChance,
                turnChance: settings.turnChance,
                cyclePrizePool: settings.cyclePrizePool,
            });
        }
        setSettingsModalOpen(true);
    };

    const saveSettings = async (values) => {
        const res = await putRequest("/api/admin/piggy-mine/settings", values, token);
        if (res?.success) {
            setSettings(res.data.data);
            setSettingsModalOpen(false);
            notification.success({ message: "Настройки сохранены" });
        } else {
            notification.error({ message: "Ошибка сохранения" });
        }
    };

    if (loading) {
        return <div style={{ textAlign: "center", padding: 60 }}><Spin size="large" /></div>;
    }

    return (
        <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <Space>
                    <Badge status={settings?.enabled ? "success" : "default"} />
                    <Tag color={settings?.enabled ? "green" : "volcano"}>
                        {settings?.enabled ? "ВКЛЮЧЕНА" : "ВЫКЛЮЧЕНА"}
                    </Tag>
                </Space>
                <Space>
                    <Switch
                        checked={settings?.enabled}
                        onChange={toggleEnabled}
                        checkedChildren="ON"
                        unCheckedChildren="OFF"
                    />
                    <Button icon={<SettingOutlined />} onClick={openSettingsModal}>Настройки</Button>
                    <Button icon={<PlayCircleOutlined />} type="primary" onClick={() => setDemoModalOpen(true)}>Демо</Button>
                    <Button icon={<ReloadOutlined />} onClick={() => { fetchSettings(); fetchStats(); }} />
                </Space>
            </div>

            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                    <Card><Statistic title="Игр сыграно" value={settings?.stats?.gamesPlayed || 0} /></Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card><Statistic title="Всего выиграно" value={((settings?.stats?.totalWon || 0) * 0.05).toFixed(1)} suffix="BRU" /></Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card><Statistic title="Собрано входных" value={((settings?.stats?.totalEntryFees || 0) * 0.05).toFixed(1)} suffix="BRU" /></Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card><Statistic title="Ср. дистанция" value={settings?.stats?.averageDistance || 0} suffix="сегм." /></Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col xs={24} sm={8}>
                    <Card title="Основные" size="small">
                        <p><Text type="secondary">Вход:</Text> {((settings?.entryFee || 0) * 0.05).toFixed(2)} BRU</p>
                        <p><Text type="secondary">Макс. сегм.:</Text> {settings?.maxSegments}</p>
                        <p><Text type="secondary">Пул:</Text> {settings?.cyclePrizePool} BRU</p>
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card title="Призы" size="small">
                        <p><Text type="secondary">Монеты:</Text> {((settings?.coinPrizeMin || 0) * 0.05).toFixed(2)}-{((settings?.coinPrizeMax || 0) * 0.05).toFixed(2)} BRU</p>
                        <p><Text type="secondary">$:</Text> {((settings?.dollarCoinPrize || 0) * 0.05).toFixed(2)} BRU</p>
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card title="Шансы" size="small">
                        <p><Text type="secondary">$:</Text> {settings?.dollarCoinChance}%</p>
                        <p><Text type="secondary">Динамит:</Text> {settings?.dynamiteChance}%</p>
                        <p><Text type="secondary">Скаттер:</Text> {settings?.scatterChance}%</p>
                    </Card>
                </Col>
            </Row>

            {stats?.recentSessions?.length > 0 && (
                <Card title="Последние сессии" size="small" style={{ marginTop: 16 }}>
                    {stats.recentSessions.slice(0, 10).map((s) => (
                        <div key={s.sessionId} style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "6px 12px", borderRadius: 6, background: "rgba(255,255,255,0.04)", marginBottom: 6,
                        }}>
                            <Space>
                                <Tag>#{s.sessionId}</Tag>
                                <Text type="secondary">{s.distance} сегм.</Text>
                            </Space>
                            <Space>
                                <Tag color={s.status === "completed" ? "green" : "blue"}>{s.endReason || s.status}</Tag>
                                <Text strong>{((s.finalPrize || 0) * 0.05).toFixed(2)} BRU</Text>
                            </Space>
                        </div>
                    ))}
                </Card>
            )}

            <Modal title="Настройки Piggy Mine" open={settingsModalOpen} onCancel={() => setSettingsModalOpen(false)} onOk={() => form.submit()} width={700} okText="Сохранить">
                <Form form={form} layout="vertical" onFinish={saveSettings}>
                    <Divider orientation="left">Основные</Divider>
                    <Row gutter={16}>
                        <Col span={8}><Form.Item label="Вход (пятаки)" name="entryFee"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item></Col>
                        <Col span={8}><Form.Item label="Макс. сегментов" name="maxSegments"><InputNumber min={50} max={1000} style={{ width: "100%" }} /></Form.Item></Col>
                        <Col span={8}><Form.Item label="Пул (BRU)" name="cyclePrizePool"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item></Col>
                    </Row>
                    <Divider orientation="left">Призы (пятаки)</Divider>
                    <Row gutter={16}>
                        <Col span={6}><Form.Item label="Монета мин" name="coinPrizeMin"><InputNumber min={1} style={{ width: "100%" }} /></Form.Item></Col>
                        <Col span={6}><Form.Item label="Монета макс" name="coinPrizeMax"><InputNumber min={1} style={{ width: "100%" }} /></Form.Item></Col>
                        <Col span={6}><Form.Item label="$ монета" name="dollarCoinPrize"><InputNumber min={1} style={{ width: "100%" }} /></Form.Item></Col>
                        <Col span={6}><Form.Item label="$ шанс (%)" name="dollarCoinChance"><InputNumber min={0} max={100} style={{ width: "100%" }} /></Form.Item></Col>
                    </Row>
                    <Divider orientation="left">Шансы (%)</Divider>
                    <Row gutter={16}>
                        <Col span={6}><Form.Item label="Динамит" name="dynamiteChance"><InputNumber min={0} max={100} style={{ width: "100%" }} /></Form.Item></Col>
                        <Col span={6}><Form.Item label="Скаттер" name="scatterChance"><InputNumber min={0} max={100} style={{ width: "100%" }} /></Form.Item></Col>
                        <Col span={6}><Form.Item label="Туннель" name="tunnelChance"><InputNumber min={0} max={100} style={{ width: "100%" }} /></Form.Item></Col>
                        <Col span={6}><Form.Item label="Поворот" name="turnChance"><InputNumber min={0} max={100} style={{ width: "100%" }} /></Form.Item></Col>
                    </Row>
                    <Divider orientation="left">Завалы</Divider>
                    <Row gutter={16}>
                        <Col span={12}><Form.Item label="Мин. расст." name="blockageMinDistance"><InputNumber min={5} style={{ width: "100%" }} /></Form.Item></Col>
                        <Col span={12}><Form.Item label="Первый не раньше" name="firstBlockageMinSegment"><InputNumber min={10} style={{ width: "100%" }} /></Form.Item></Col>
                    </Row>
                    <Divider orientation="left">Бонус</Divider>
                    <Row gutter={16}>
                        <Col span={6}><Form.Item label="Лампа мин (с)" name="bonusLampDurationMin"><InputNumber min={1} style={{ width: "100%" }} /></Form.Item></Col>
                        <Col span={6}><Form.Item label="Лампа макс (с)" name="bonusLampDurationMax"><InputNumber min={1} style={{ width: "100%" }} /></Form.Item></Col>
                        <Col span={6}><Form.Item label="Тап мин" name="bonusTapRewardMin"><InputNumber min={1} style={{ width: "100%" }} /></Form.Item></Col>
                        <Col span={6}><Form.Item label="Тап макс" name="bonusTapRewardMax"><InputNumber min={1} style={{ width: "100%" }} /></Form.Item></Col>
                    </Row>
                </Form>
            </Modal>

            <Modal title="Piggy Mine — Симуляция" open={demoModalOpen} onCancel={() => setDemoModalOpen(false)} footer={null} width={1000} destroyOnClose>
                <PiggyMineDemo settings={settings} />
            </Modal>
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PIGGY MINE SIMULATION ENGINE (100 players, math model)
// ═══════════════════════════════════════════════════════════════════════════════

function _randInt(min, max) { return Math.floor(Math.random() * (max - min + 1)) + min; }
function _shuffle(arr) { for (let i = arr.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [arr[i], arr[j]] = [arr[j], arr[i]]; } return arr; }
function _avg(arr) { return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0; }
function _median(arr) { const s = [...arr].sort((a, b) => a - b); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; }

function _generateTrack(cfg) {
    const track = [];
    let lastBlockageAt = -cfg.blockageMinDistance;
    let dynamitesOnTrack = 0;
    let scatterCounts = { pickaxe: 0, lamp: 0, shovel: 0 };
    let inTunnel = false;
    let tunnelLanesLeft = 0;
    let tunnelActiveLanes = [0, 1, 2];

    for (let i = 0; i < cfg.maxSegments; i++) {
        let activeLanes = [0, 1, 2];

        if (inTunnel) {
            tunnelLanesLeft--;
            activeLanes = tunnelActiveLanes;
            if (tunnelLanesLeft <= 0) inTunnel = false;
        } else {
            const progress = i / cfg.maxSegments;
            const tChance = cfg.tunnelChance * (1 + progress * 0.5);
            const turnCh = cfg.turnChance * (1 + progress * 0.5);
            if (i > 10 && Math.random() * 100 < tChance) {
                const side = Math.random() < 0.5 ? 'left' : 'right';
                tunnelActiveLanes = side === 'left' ? [0] : [2];
                activeLanes = tunnelActiveLanes;
                inTunnel = true;
                tunnelLanesLeft = _randInt(3, 6);
            } else if (i > 8 && Math.random() * 100 < turnCh) {
                const side = Math.random() < 0.5 ? 'left' : 'right';
                activeLanes = side === 'left' ? [0, 1] : [1, 2];
            }
        }

        const lanes = [{ item: 'empty', prize: 0 }, { item: 'empty', prize: 0 }, { item: 'empty', prize: 0 }];
        const distSince = i - lastBlockageAt;
        const canBlock = i >= cfg.firstBlockageMinSegment && distSince >= cfg.blockageMinDistance && dynamitesOnTrack > 0;
        const progress = i / cfg.maxSegments;
        const blockChance = 15 + progress * 35;

        if (canBlock && Math.random() * 100 < blockChance) {
            const totalW = 25 + 40 + 35;
            let r = Math.random() * totalW;
            let blockCount = r < 25 ? 1 : r < 65 ? 2 : 3;
            blockCount = Math.min(blockCount, activeLanes.length);
            const shuffled = _shuffle([...activeLanes]);
            for (let b = 0; b < blockCount; b++) lanes[shuffled[b]] = { item: 'blockage', prize: 0 };
            lastBlockageAt = i;
        } else {
            for (const l of activeLanes) {
                if (lanes[l].item !== 'empty') continue;
                const roll = Math.random() * 100;
                let cum = 0;
                cum += cfg.dynamiteChance;
                if (roll < cum) { lanes[l] = { item: 'dynamite', prize: 0 }; dynamitesOnTrack++; continue; }
                cum += cfg.scatterChance;
                if (roll < cum) {
                    const types = ['pickaxe', 'lamp', 'shovel'];
                    const min = Math.min(scatterCounts.pickaxe, scatterCounts.lamp, scatterCounts.shovel);
                    const cands = types.filter(t => scatterCounts[t] === min);
                    const sType = cands[_randInt(0, cands.length - 1)];
                    lanes[l] = { item: `scatter_${sType}`, prize: 0 };
                    scatterCounts[sType]++;
                    continue;
                }
                cum += cfg.dollarCoinChance;
                if (roll < cum) { lanes[l] = { item: 'coin_dollar', prize: cfg.dollarCoinPrize }; continue; }
                if (Math.random() * 100 < 15) {
                    lanes[l] = { item: 'coin_suit', prize: _randInt(cfg.coinPrizeMin, cfg.coinPrizeMax) };
                }
            }
        }
        if (i === cfg.firstBlockageMinSegment - 5 && dynamitesOnTrack === 0) {
            const tl = activeLanes[_randInt(0, activeLanes.length - 1)];
            lanes[tl] = { item: 'dynamite', prize: 0 };
            dynamitesOnTrack++;
        }
        track.push({ lanes, activeLanes });
    }
    return track;
}

function _simulatePlayer(cfg) {
    const track = _generateTrack(cfg);
    let lane = 1, dynamite = 0, coins = 0, distance = 0;
    let scatters = { pickaxe: false, lamp: false, shovel: false };
    let bonusCount = 0, bonusReward = 0, dynamiteUsed = 0, blockagesEncountered = 0;

    for (let i = 0; i < track.length; i++) {
        const { activeLanes, lanes } = track[i];
        if (!activeLanes.includes(lane)) lane = activeLanes[_randInt(0, activeLanes.length - 1)];
        if (activeLanes.length === 1) lane = activeLanes[0];

        const blockedLanes = activeLanes.filter(l => lanes[l]?.item === 'blockage');
        const safeLanes = activeLanes.filter(l => lanes[l]?.item !== 'blockage');

        if (blockedLanes.length > 0) {
            blockagesEncountered++;
            if (blockedLanes.length >= activeLanes.length) {
                if (dynamite > 0) { dynamite--; dynamiteUsed++; }
                else return { distance: i, coins, dynamiteUsed, bonusCount, bonusReward, endReason: 'blockage', blockagesEncountered };
            } else if (blockedLanes.includes(lane)) {
                if (Math.random() < 0.60 && safeLanes.length > 0) lane = safeLanes[_randInt(0, safeLanes.length - 1)];
                else if (dynamite > 0) { dynamite--; dynamiteUsed++; }
                else return { distance: i, coins, dynamiteUsed, bonusCount, bonusReward, endReason: 'blockage', blockagesEncountered };
            }
        } else {
            if (Math.random() < 0.60 && activeLanes.length > 1) {
                let bestLane = lane, bestScore = _scoreLane(lanes[lane], dynamite, scatters);
                for (const l of activeLanes) {
                    if (l === lane) continue;
                    const s = _scoreLane(lanes[l], dynamite, scatters);
                    if (s > bestScore + 3) { bestScore = s; bestLane = l; }
                }
                lane = bestLane;
            }
        }

        const item = lanes[lane]?.item || 'empty';
        if (item === 'dynamite') dynamite++;
        else if (item === 'coin_suit' || item === 'coin_dollar') coins += lanes[lane].prize;
        else if (item === 'scatter_pickaxe') scatters.pickaxe = true;
        else if (item === 'scatter_lamp') scatters.lamp = true;
        else if (item === 'scatter_shovel') scatters.shovel = true;

        if (scatters.pickaxe && scatters.lamp && scatters.shovel) {
            scatters = { pickaxe: false, lamp: false, shovel: false };
            bonusCount++;
            let bonus = 0;
            for (let phase = 0; phase < 2; phase++) {
                const lamp = _randInt((cfg.bonusLampDurationMin || 3) * 1000, (cfg.bonusLampDurationMax || 8) * 1000) / 1000;
                const taps = Math.floor(lamp * 5);
                for (let t = 0; t < taps; t++) bonus += _randInt(cfg.bonusTapRewardMin || 2, cfg.bonusTapRewardMax || 10);
            }
            bonusReward += bonus;
            coins += bonus;
        }
        distance = i + 1;
    }
    return { distance, coins, dynamiteUsed, bonusCount, bonusReward, endReason: 'max_distance', blockagesEncountered };
}

function _scoreLane(laneData, dynamite, scatters) {
    if (!laneData) return 0;
    switch (laneData.item) {
        case 'blockage': return dynamite > 0 ? -50 : -1000;
        case 'dynamite': return 80;
        case 'scatter_pickaxe': return scatters.pickaxe ? 10 : 60;
        case 'scatter_lamp': return scatters.lamp ? 10 : 60;
        case 'scatter_shovel': return scatters.shovel ? 10 : 60;
        case 'coin_dollar': return 70;
        case 'coin_suit': return 20 + (laneData.prize || 0);
        default: return 0;
    }
}

const PiggyMineDemo = ({ settings }) => {
    const [running, setRunning] = useState(false);
    const [simData, setSimData] = useState(null);

    const runSim = () => {
        setRunning(true);
        setTimeout(() => {
            const cfg = {
                maxSegments: settings?.maxSegments || 200,
                entryFee: settings?.entryFee || 100,
                coinPrizeMin: settings?.coinPrizeMin || 1,
                coinPrizeMax: settings?.coinPrizeMax || 20,
                dollarCoinPrize: settings?.dollarCoinPrize || 500,
                dollarCoinChance: settings?.dollarCoinChance || 3,
                dynamiteChance: 5,
                scatterChance: settings?.scatterChance || 5,
                blockageMinDistance: 10,
                firstBlockageMinSegment: 15,
                tunnelChance: settings?.tunnelChance || 8,
                turnChance: settings?.turnChance || 6,
                bonusLampDurationMin: settings?.bonusLampDurationMin || 3,
                bonusLampDurationMax: settings?.bonusLampDurationMax || 8,
                bonusTapRewardMin: settings?.bonusTapRewardMin || 2,
                bonusTapRewardMax: settings?.bonusTapRewardMax || 10,
                cyclePrizePool: settings?.cyclePrizePool || 10000,
            };

            const results = [];
            for (let i = 0; i < 100; i++) results.push(_simulatePlayer(cfg));

            const distances = results.map(r => r.distance);
            const prizes = results.map(r => Math.floor(r.coins * 0.05));

            const totalEntryFees = 100 * cfg.entryFee;
            const totalPayout = prizes.reduce((a, b) => a + b, 0);
            const winners = results.filter((r, idx) => prizes[idx] >= cfg.entryFee).length;

            setSimData({
                results, cfg,
                s: {
                    avgDist: _avg(distances).toFixed(1),
                    medDist: _median(distances),
                    minDist: Math.min(...distances),
                    maxDist: Math.max(...distances),
                    avgPrize: _avg(prizes).toFixed(0),
                    avgPrizeBRU: (_avg(prizes) * 0.05).toFixed(2),
                    medPrize: _median(prizes),
                    minPrize: Math.min(...prizes),
                    maxPrize: Math.max(...prizes),
                    endedBlock: results.filter(r => r.endReason === 'blockage').length,
                    reachedMax: results.filter(r => r.endReason === 'max_distance').length,
                    hadBonus: results.filter(r => r.bonusCount > 0).length,
                    avgBonus: _avg(results.map(r => r.bonusCount)).toFixed(2),
                    avgDynUsed: _avg(results.map(r => r.dynamiteUsed)).toFixed(1),
                    avgBlocks: _avg(results.map(r => r.blockagesEncountered)).toFixed(1),
                    totalEntry: totalEntryFees,
                    totalPay: totalPayout,
                    houseEdge: ((totalEntryFees - totalPayout) / totalEntryFees * 100).toFixed(1),
                    rtp: (totalPayout / totalEntryFees * 100).toFixed(1),
                    netProfit: totalEntryFees - totalPayout,
                    netBRU: ((totalEntryFees - totalPayout) * 0.05).toFixed(2),
                    winners,
                    losers: 100 - winners,
                    entryBRU: (cfg.entryFee * 0.05).toFixed(2),
                }
            });
            setRunning(false);
        }, 50);
    };

    if (!simData) {
        return (
            <div style={{ textAlign: "center", padding: 40 }}>
                <Title level={4}>Piggy Mine — Симуляция 100 игроков</Title>
                <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                    Математический просчёт: эмуляция 100 игроков по текущим настройкам
                </Text>
                <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
                    Без визуализации — чистая математика и логи каждого игрока
                </Text>
                <Button type="primary" size="large" icon={<ThunderboltOutlined />} onClick={runSim} loading={running}>
                    Запустить симуляцию
                </Button>
            </div>
        );
    }

    const { results, cfg, s } = simData;

    return (
        <div style={{ maxHeight: "70vh", overflowY: "auto", padding: "0 8px" }}>
            <Title level={4} style={{ textAlign: "center", marginBottom: 16 }}>Результаты — 100 игроков</Title>

            <Card title="Экономика цикла (100 игр)" size="small" style={{ marginBottom: 16 }}>
                <Row gutter={[16, 12]}>
                    <Col span={6}><Statistic title="Вход за игру" value={s.entryBRU} suffix="BRU" /></Col>
                    <Col span={6}><Statistic title="Собрано входных" value={(s.totalEntry * 0.05).toFixed(1)} suffix="BRU" /></Col>
                    <Col span={6}><Statistic title="Выплачено" value={(s.totalPay * 0.05).toFixed(1)} suffix="BRU" /></Col>
                    <Col span={6}><Statistic title="Прибыль" value={s.netBRU} suffix="BRU" valueStyle={{ color: s.netProfit >= 0 ? '#3f8600' : '#cf1322' }} /></Col>
                </Row>
                <Divider style={{ margin: "12px 0" }} />
                <Row gutter={[16, 12]}>
                    <Col span={6}><Statistic title="RTP" value={s.rtp} suffix="%" /></Col>
                    <Col span={6}><Statistic title="House Edge" value={s.houseEdge} suffix="%" /></Col>
                    <Col span={6}><Statistic title="Выиграли" value={s.winners} suffix="/ 100" valueStyle={{ color: '#3f8600' }} /></Col>
                    <Col span={6}><Statistic title="Проиграли" value={s.losers} suffix="/ 100" valueStyle={{ color: '#cf1322' }} /></Col>
                </Row>
            </Card>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={12}>
                    <Card title="Дистанция (сегменты)" size="small">
                        <p><Text type="secondary">Средняя:</Text> <Text strong>{s.avgDist}</Text></p>
                        <p><Text type="secondary">Медиана:</Text> <Text strong>{s.medDist}</Text></p>
                        <p><Text type="secondary">Мин / Макс:</Text> {s.minDist} / {s.maxDist}</p>
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="Призы" size="small">
                        <p><Text type="secondary">Средний:</Text> <Text strong>{s.avgPrizeBRU} BRU</Text></p>
                        <p><Text type="secondary">Медиана:</Text> {(s.medPrize * 0.05).toFixed(2)} BRU</p>
                        <p><Text type="secondary">Мин / Макс:</Text> {(s.minPrize * 0.05).toFixed(2)} / {(s.maxPrize * 0.05).toFixed(2)} BRU</p>
                    </Card>
                </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                    <Card title="Окончания" size="small">
                        <p><Tag color="red">Завалы:</Tag> {s.endedBlock} ({s.endedBlock}%)</p>
                        <p><Tag color="green">Макс дист.:</Tag> {s.reachedMax} ({s.reachedMax}%)</p>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card title="Бонусы" size="small">
                        <p><Text type="secondary">С бонусом:</Text> {s.hadBonus}</p>
                        <p><Text type="secondary">Сред. кол-во:</Text> {s.avgBonus}</p>
                    </Card>
                </Col>
                <Col span={8}>
                    <Card title="Динамит" size="small">
                        <p><Text type="secondary">Использовано:</Text> {s.avgDynUsed}</p>
                        <p><Text type="secondary">Завалов встречено:</Text> {s.avgBlocks}</p>
                    </Card>
                </Col>
            </Row>

            <Card title="Логи всех 100 игроков" size="small" style={{ marginBottom: 16 }} bodyStyle={{ maxHeight: 400, overflowY: "auto", padding: "8px 16px" }}>
                <div style={{ fontFamily: "monospace", fontSize: 12, lineHeight: "22px" }}>
                    <div style={{ display: "flex", padding: "4px 0", borderBottom: "1px solid rgba(255,255,255,0.15)", fontWeight: "bold", position: "sticky", top: 0, background: "#141414", zIndex: 1, color: "#fff" }}>
                        <span style={{ width: 36 }}>#</span>
                        <span style={{ width: 60 }}>Дист.</span>
                        <span style={{ width: 90 }}>Приз BRU</span>
                        <span style={{ width: 70 }}>Итог</span>
                        <span style={{ width: 80 }}>Причина</span>
                        <span style={{ width: 55 }}>Бонус</span>
                        <span style={{ width: 45 }}>Дин.</span>
                        <span style={{ flex: 1 }}>Нетто BRU</span>
                    </div>
                    {results.map((r, idx) => {
                        const actualPrize = Math.floor(r.coins * 0.05);
                        const isWin = actualPrize >= cfg.entryFee;
                        return (
                            <div key={idx} style={{ display: "flex", padding: "2px 0", borderBottom: "1px solid rgba(255,255,255,0.05)", color: isWin ? "#52c41a" : "#ff4d4f" }}>
                                <span style={{ width: 36, color: "#888" }}>{idx + 1}</span>
                                <span style={{ width: 60 }}>{r.distance}</span>
                                <span style={{ width: 90 }}>{(actualPrize * 0.05).toFixed(2)}</span>
                                <span style={{ width: 70 }}>{isWin ? "WIN" : "LOSS"}</span>
                                <span style={{ width: 80, color: r.endReason === 'blockage' ? '#ff7875' : '#95de64' }}>{r.endReason}</span>
                                <span style={{ width: 55 }}>{r.bonusCount}</span>
                                <span style={{ width: 45 }}>{r.dynamiteUsed}</span>
                                <span style={{ flex: 1, fontWeight: "bold" }}>{((actualPrize - cfg.entryFee) * 0.05).toFixed(2)}</span>
                            </div>
                        );
                    })}
                </div>
            </Card>

            <div style={{ textAlign: "center", paddingBottom: 16 }}>
                <Space>
                    <Button type="primary" icon={<ReloadOutlined />} onClick={runSim} loading={running}>Перезапустить</Button>
                    <Button onClick={() => setSimData(null)}>Сбросить</Button>
                </Space>
            </div>
        </div>
    );
};

export { routeGames };

export default GamesPage;
